import { useEffect } from 'react';
import { useAudioPlayer } from 'react-use-audio-player';
import kalshiFillSound from 'src/Sounds/kalshi-fill.mp3';
import polyFillSound from 'src/Sounds/poly-fill.mp3';
import alarmSound from 'src/Sounds/error.mp3';
import { useNotificationsSocket } from '../../hooks/useNotificationsSocket';
import { useNotificationSound } from '../../hooks/useNotificationSound';

/**
 * App-wide prediction-notification SOUND mount.
 *
 * Renders nothing. Mounted ONCE in the predictions sidebar (next to the
 * `NotificationBell`, gated on `mode === 'predictions'`) so the fill /
 * alarm cues play on EVERY prediction page, not just
 * `/predictions/notifications` and `/predictions/opportunities`. Before
 * this, the sound hook was page-scoped and the operator missed every fill
 * and alarm while on Positions / Unwinding / Opening / etc.
 *
 * Why mounting here does NOT open a second WebSocket: `NotificationBell`
 * already keeps a `useNotificationsSocket()` alive in predictions mode,
 * and `socket.io-react-hook`'s `IoProvider` ref-counts connections per
 * URL — this second `useNotificationsSocket()` reuses the same socket.
 *
 * Cross-tab: each open browser tab rings once. That is the SAME behavior
 * as the old page-scoped mounts and is acceptable. We deliberately do NOT
 * reintroduce a Web-Locks leader election (reverted 2026-05-23 — it both
 * went silent on a backgrounded tab and double-rang on HMR remounts).
 *
 * Autoplay priming: browsers swallow audio before the first user gesture,
 * so the very first real fill/alarm cue could be dropped. We attach a
 * one-time `pointerdown` listener that silently primes all three players
 * (zero-volume load) and removes itself — idempotent, gesture-driven.
 */
export function PredictionNotificationSound(): null {
  const { latestEvent, acknowledge } = useNotificationsSocket();
  useNotificationSound(latestEvent);

  useEffect(() => {
    if (!latestEvent) return;
    acknowledge();
  }, [latestEvent, acknowledge]);

  // One-time autoplay unlock. `useAudioPlayer()` returns a fresh wrapper
  // each render but its `load`/`stop` callbacks are stable — destructure
  // them up front and depend only on those (never the wrapper object), per
  // the documented unstable-wrapper gotcha in useNotificationSound.
  const { load: loadKalshi, stop: stopKalshi } = useAudioPlayer();
  const { load: loadPoly, stop: stopPoly } = useAudioPlayer();
  const { load: loadMain, stop: stopMain } = useAudioPlayer();

  useEffect(() => {
    let primed = false;
    const prime = () => {
      if (primed) return;
      primed = true;
      try {
        loadKalshi(kalshiFillSound, { autoplay: true, initialVolume: 0 });
        loadPoly(polyFillSound, { autoplay: true, initialVolume: 0 });
        loadMain(alarmSound, { autoplay: true, initialVolume: 0 });
        // Stop immediately — we only needed the gesture-driven load() to
        // unlock the browser audio context, not to actually play.
        stopKalshi();
        stopPoly();
        stopMain();
      } catch {
        // Priming is best-effort; a real fill will still attempt to play.
      }
      window.removeEventListener('pointerdown', prime);
    };
    window.addEventListener('pointerdown', prime, { once: true });
    return () => window.removeEventListener('pointerdown', prime);
  }, [loadKalshi, stopKalshi, loadPoly, stopPoly, loadMain, stopMain]);

  return null;
}
